import { useState } from 'react';
import { Tabs as TabsComponent, Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { AvailableFeatureFlags, NewsletterDto, PublicationSetting } from 'data/generated/graphql';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import styles from '../../NewsletterEdit.module.scss';
import ContentSearch from '../content-search/ContentSearch';
import { NewsletterSettings } from '../newsletter-settings/NewsletterSettings';

enum Tabs {
  Articles,
  Embargoed,
  Settings
}

interface NewsletterMultitabSectionProps {
  campaign: NewsletterDto;
  readOnly?: boolean;
  newsletterName?: string;
  isScheduled?: boolean;
  cancelCampaign?: () => void;
  publicationSetting?: PublicationSetting;
}

export const NewsletterMultitabSection = ({
  campaign,
  readOnly,
  isScheduled = false,
  newsletterName,
  cancelCampaign,
  publicationSetting
}: NewsletterMultitabSectionProps) => {
  const [tabIndex, setTabIndex] = useState(readOnly ? Tabs.Settings : Tabs.Articles);
  const isEmbargoTabEnabled = useFeatureFlag(AvailableFeatureFlags.NewsletterEmbargoTabEnabled, true);

  return (
    <Wrapper>
      <TabsComponent role="tablist" onChange={setTabIndex} value={tabIndex} margin={{ bottom: 'md' }}>
        <TabsComponent.Item
          role="tab"
          id="tab-id0"
          aria-selected={tabIndex === Tabs.Articles ? 'true' : 'false'}
          aria-controls="tabpanel-id0"
          disabled={readOnly}
          className={classnames({ [styles.disabledTab]: readOnly })}
        >
          Articles
        </TabsComponent.Item>
        {isEmbargoTabEnabled && (
          <TabsComponent.Item
            role="tab"
            id="tab-id1"
            aria-selected={tabIndex === Tabs.Embargoed ? 'true' : 'false'}
            aria-controls="tabpanel-id1"
            disabled={readOnly}
            className={classnames({ [styles.disabledTab]: readOnly })}
          >
            Embargoed
          </TabsComponent.Item>
        )}
        <TabsComponent.Item
          role="tab"
          id="tab-id2"
          aria-selected={tabIndex === Tabs.Settings ? 'true' : 'false'}
          aria-controls="tabpanel-id2"
          disabled={!(readOnly && campaign)}
          className={classnames({ [styles.disabledTab]: !readOnly })}
          data-testid="newsletters-settings-tab"
        >
          Settings
        </TabsComponent.Item>
      </TabsComponent>
      {tabIndex === Tabs.Articles && !readOnly && (
        <Wrapper id="tabpanel-id0" role="tabpanel" aria-labelledby="tab-id0" padding={{ top: 'sm' }}>
          <ContentSearch newsletterName={newsletterName} />
        </Wrapper>
      )}
      {tabIndex === Tabs.Embargoed && !readOnly && isEmbargoTabEnabled && (
        <Wrapper id="tabpanel-id1" role="tabpanel" aria-labelledby="tab-id1" padding={{ top: 'sm' }}>
          <Typography
            variant="header3"
            margin={{ bottom: 'none' }}
            data-testid="page-multi-tab-section-embargoed-tab-title"
          >
            Embargoed
          </Typography>
          <ContentSearch newsletterName={newsletterName} embargo />
        </Wrapper>
      )}
      {tabIndex === Tabs.Settings && (
        <Wrapper id="tabpanel-id2" role="tabpanel" aria-labelledby="tab-id2" padding={{ top: 'sm' }}>
          <NewsletterSettings
            readOnly={readOnly}
            isScheduled={isScheduled}
            campaign={campaign}
            cancelCampaign={cancelCampaign}
            publicationSetting={publicationSetting}
          />
        </Wrapper>
      )}
    </Wrapper>
  );
};
